import { firestoreConverter } from './converter';
import { FirestoreSession } from './session';
import { FirestoreCard, StepHistoryEvent, CardEvent } from './card';
import { FirestoreComment } from './comment';
import { FirestoreSource } from './source';
import { EnvFirestore, CollectionRef, DocumentRef } from './types';
import { FirestoreSourceRow } from './row';
import { FirestoreUser, FirestoreUserSettings } from './userSettings';
import {
  FirestoreOrganization,
  FirestoreOrganizationMember,
  FirestoreSessionPermission,
  FirestoreAccessRequest,
  FirestoreDuplicationRequest,
  FirestoreBugReport,
  FirestoreTeam,
  FirestoreTeamMember,
} from './permissions';
import { OrganizationClaimToken, UserInvitationStatus, AdminAuditLog, CQAdminUser } from './admin';
import { FirestoreAssetCollection, FirestoreCollectionMember } from './assetCollection';

export const firestoreCollections = <F>(fb: EnvFirestore) => ({
  sessions: fb.collection('sessions').withConverter(firestoreConverter) as CollectionRef<
    F,
    FirestoreSession
  >,
  sessionCards: (sessionId: string) =>
    fb
      .collection(`sessions/${sessionId}/cardList`)
      .withConverter(firestoreConverter) as CollectionRef<F, FirestoreCard>,
  // Multi-Step Queue: Step history audit trail for a card
  cardStepHistory: (sessionId: string, cardId: string) =>
    fb
      .collection(`sessions/${sessionId}/cardList/${cardId}/stepHistory`)
      .withConverter(firestoreConverter) as CollectionRef<F, StepHistoryEvent>,
  // Card-level event history for the History section
  cardEvents: (sessionId: string, cardId: string) =>
    fb
      .collection(`sessions/${sessionId}/cardList/${cardId}/events`)
      .withConverter(firestoreConverter) as CollectionRef<F, CardEvent>,
  sessionComments: (sessionId: string) =>
    fb
      .collection(`sessions/${sessionId}/comments`)
      .withConverter(firestoreConverter) as CollectionRef<F, FirestoreComment>,
  sessionSources: (sessionId: string) =>
    fb
      .collection(`sessions/${sessionId}/sources`)
      .withConverter(firestoreConverter) as CollectionRef<F, FirestoreSource>,
  sessionSourceRows: (sessionId: string, sourceId: string) =>
    fb
      .collection(`sessions/${sessionId}/sources/${sourceId}/rows`)
      .withConverter(firestoreConverter) as CollectionRef<F, FirestoreSourceRow>,
  sessionUserSettings: (sessionId: string, userId: string) =>
    fb
      .doc(`sessions/${sessionId}/userSettings/${userId}`)
      .withConverter(firestoreConverter) as DocumentRef<F, FirestoreUserSettings>,

  // NEW: Organization collections
  organizations: fb.collection('organizations').withConverter(firestoreConverter) as CollectionRef<
    F,
    FirestoreOrganization
  >,
  organizationMembers: (orgId: string) =>
    fb
      .collection(`organizations/${orgId}/members`)
      .withConverter(firestoreConverter) as CollectionRef<F, FirestoreOrganizationMember>,

  // NEW: Team collections
  teams: (orgId: string) =>
    fb
      .collection(`organizations/${orgId}/teams`)
      .withConverter(firestoreConverter) as CollectionRef<F, FirestoreTeam>,
  teamMembers: (orgId: string, teamId: string) =>
    fb
      .collection(`organizations/${orgId}/teams/${teamId}/members`)
      .withConverter(firestoreConverter) as CollectionRef<F, FirestoreTeamMember>,

  // NEW: Session permission collections
  sessionPermissions: (sessionId: string) =>
    fb
      .collection(`sessions/${sessionId}/permissions`)
      .withConverter(firestoreConverter) as CollectionRef<F, FirestoreSessionPermission>,

  // NEW: Access request collections
  accessRequests: fb
    .collection('accessRequests')
    .withConverter(firestoreConverter) as CollectionRef<F, FirestoreAccessRequest>,

  // NEW: Duplication request collections
  duplicationRequests: fb
    .collection('duplicationRequests')
    .withConverter(firestoreConverter) as CollectionRef<F, FirestoreDuplicationRequest>,

  // Bug reports (Report Bug feature)
  bugReports: fb.collection('bugReports').withConverter(firestoreConverter) as CollectionRef<
    F,
    FirestoreBugReport
  >,

  users: (userId: string) =>
    fb.doc(`users/${userId}`).withConverter(firestoreConverter) as DocumentRef<F, FirestoreUser>,

  // Admin dashboard collections
  claimTokens: fb.collection('claimTokens').withConverter(firestoreConverter) as CollectionRef<
    F,
    OrganizationClaimToken
  >,
  userInvitations: fb
    .collection('userInvitations')
    .withConverter(firestoreConverter) as CollectionRef<F, UserInvitationStatus>,
  adminAuditLogs: fb
    .collection('adminAuditLogs')
    .withConverter(firestoreConverter) as CollectionRef<F, AdminAuditLog>,
  cqAdmins: fb.collection('cqAdmins').withConverter(firestoreConverter) as CollectionRef<
    F,
    CQAdminUser
  >,

  assetCollections: fb.collection('collections').withConverter(firestoreConverter) as CollectionRef<
    F,
    FirestoreAssetCollection
  >,
  collectionMembers: (collectionId: string) =>
    fb
      .collection(`collections/${collectionId}/members`)
      .withConverter(firestoreConverter) as CollectionRef<F, FirestoreCollectionMember>,
});
